import { IconCheck, IconPlus } from "@tabler/icons-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { colors } from "#app/theme/v2/colors";
import { radius } from "#app/theme/v2/radius";
import { spacing } from "#app/theme/v2/spacing";
import { Divider } from "#components/atoms/Divider/Divider";
import { Select } from "#components/molecules/Select/Select";
import { AddMaterialFormatModal } from "#modules/layoutsV2/view/AddMaterialFormatModal";
import { useMaterialDimensionsSelect } from "#modules/quoteRequestLines/views/DetailsPanel/Material/useMaterialDimensionsSelect";
import {
  FormatterMaterialParameter,
  MaterialGradeType,
  MaterialLayout,
  PartTypeType,
} from "shared";

const ADD_FORMAT_OPTION = "ADD_FORMAT";

const OPTION_ROW_STYLE: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  width: "100%",
};

const AddFormatOption = ({ label }: { label: string }) => (
  <div
    data-select-footer
    style={{
      display: "flex",
      flexDirection: "column",
      gap: spacing[2],
      width: "100%",
      paddingTop: spacing[2],
    }}
  >
    <Divider />
    <div
      data-select-footer-row
      style={{
        display: "flex",
        alignItems: "center",
        gap: spacing[2],
        padding: `${spacing[2]} ${spacing[3]}`,
        borderRadius: radius.xs,
      }}
    >
      <IconPlus size={16} color={colors.icon.primaryDefault} />
      <span>{label}</span>
    </div>
  </div>
);

type Props = {
  materialLayout: MaterialLayout;
  materialGrade: MaterialGradeType;
  materialName: string;
  quoteRequestLineId: string | undefined;
  partType: PartTypeType | null;
  partSubType: string | null;
  partDimensionsWithOverthickness: FormatterMaterialParameter;
  selectedMaterialId: string | null;
  onSelectMaterial: (newMaterialId: string | null) => void;
};

export const DimensionsSelect = ({
  selectedMaterialId,
  onSelectMaterial,
  quoteRequestLineId,
  ...params
}: Props) => {
  const { t } = useTranslation();
  const [isAddFormatModalOpened, setIsAddFormatModalOpened] = useState(false);
  const { dimensionGroups, expandableSelectProps, value, onChange } = useMaterialDimensionsSelect({
    ...params,
    quoteRequestLineId,
    controlled: { value: selectedMaterialId, onChange: onSelectMaterial },
  });

  const groupsWithAddFormat = dimensionGroups.map((group, index) =>
    index === dimensionGroups.length - 1
      ? {
          ...group,
          items: [
            ...group.items,
            { value: ADD_FORMAT_OPTION, label: t("layout.sidePanel.addFormat") },
          ],
        }
      : group,
  );

  const handleChange = (newValue: string | null) => {
    if (newValue === ADD_FORMAT_OPTION) {
      setIsAddFormatModalOpened(true);
      return;
    }
    onChange(newValue);
  };

  return (
    <>
      <Select
        label={t("layout.sidePanel.formatDimensions")}
        placeholder={t("qrl.dimensions.select")}
        data={groupsWithAddFormat}
        value={value}
        onChange={handleChange}
        {...expandableSelectProps}
        renderOption={({ option, checked }) =>
          option.value === ADD_FORMAT_OPTION ? (
            <AddFormatOption label={option.label} />
          ) : (
            <div style={OPTION_ROW_STYLE}>
              <span>{option.label}</span>
              {checked && <IconCheck size={16} color={colors.icon.primaryDefault} />}
            </div>
          )
        }
      />
      <AddMaterialFormatModal
        opened={isAddFormatModalOpened}
        onClose={() => setIsAddFormatModalOpened(false)}
        onFormatSaved={onSelectMaterial}
        quoteRequestLineId={quoteRequestLineId}
        {...params}
      />
    </>
  );
};
